import React, { useRef } from 'react'
import { useStyles } from './style'
import { useTheme } from '@mui/styles'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { routes } from '../../routes'
import { debounce } from 'lodash'
import { Button, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { FiltersContainer, SearchFilter } from '../../components/filters'
import SkillBiteItem from './skill-bite-item'
import { SkillBiteListSlice } from '../../reducers'

const SkillBiteList = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const navigate = useNavigate()
  const initialFilters = React.useMemo(() => ({ search: '' }), [])
  const [filters, setFilters] = React.useState(initialFilters)
  const pageSize = React.useMemo(() => 5, [])
  const loadMoreRef = useRef()

  const skillBiteList = useSelector(SkillBiteListSlice.selectors.selectSkillBiteList)
  const skillBiteTotal = useSelector(SkillBiteListSlice.selectors.selectTotal)

  const hasMoreSkillBites = React.useMemo(() => skillBiteTotal !== skillBiteList.length, [skillBiteList, skillBiteTotal])

  const scrollToBottom = () => {
    setTimeout(() => {
      if (loadMoreRef.current === null) return
      loadMoreRef.current.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const [getSkillBitesList, addSkillBiteList] = useEvent([
    SkillBiteListSlice.eventBus.getSkillBiteList({
      onSuccess: () => {
        scrollToBottom()
      }
    }),
    SkillBiteListSlice.eventBus.addSkillBiteList({
      onSuccess: () => {
        scrollToBottom()
      }
    })
  ])

  const onFetchMoreSkillBites = React.useCallback(() => {
    addSkillBiteList.trigger({
      from: skillBiteList.length,
      size: pageSize,
      filters
    })
  }, [addSkillBiteList, skillBiteList, pageSize, filters])

  const isLoading = React.useMemo(() => {
    return getSkillBitesList.isWorking || !getSkillBitesList.hasBeenTriggered
  }, [getSkillBitesList.isWorking, getSkillBitesList.hasBeenTriggered])

  const debouncedSearch = React.useMemo(() => debounce(filters => {
    getSkillBitesList.trigger({ size: pageSize, filters })
  }, 1000), [getSkillBitesList, pageSize])

  React.useEffect(() => { debouncedSearch(filters) }, [debouncedSearch, filters])

  return (
    <>
      <Grid container classes={{ root: classes.header }}>
        <Grid container direction='row' alignItems='center'>
          <Grid item style={{ flexGrow: 1 }}>
            <Typography id='skill-bites-list-title' tabIndex={0} component='div' variant='h5'>
              Skill Bites
            </Typography>
          </Grid>
          <Grid item style={{ marginRight: spacing(3) }}>
            <FiltersContainer filters={filters} onChange={setFilters}>
              <SearchFilter name='search' sx={{ background: '#FFF' }} />
            </FiltersContainer>
          </Grid>
          <Grid item>
            <Button
              id='create-skill-bite-button'
              data-test='create-skill-bite-button'
              variant='contained'
              color='primary'
              name='create-skill-bite'
              aria-label='create new skill bite'
              onClick={() => navigate(routes.skillBiteNew.path)}
            >
              New Skill Bite
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Paper
        id='skill-bite-list'
        classes={{ root: classes.tablePaper }}
        tabIndex={0}
        aria-label='Content List'
        elevation={0}
      >
        <Grid
          id='skill-bites-list-headers'
          container
          tabIndex={0}
          direction='row'
          alignItems='center'
          className={classes.gridHeader}
          classes={{ root: classes.tableHead }}
          aria-label='List headers, Title, Sessions, Creation Date, Author, Status, Preview'
        >
          <Grid item md={4} sx={{ paddingLeft: 3 }}>Title</Grid>
          <Grid item md={1}>Expert</Grid>
          <Grid item md={1}>Sessions</Grid>
          <Grid item md={2}>Creation Date</Grid>
          <Grid item md={2}>Author</Grid>
          <Grid item md={1}>Status</Grid>
          <Grid item md={1}>Preview</Grid>
        </Grid>
        {(isLoading) && (
          <Grid
            id='skill-bites-list-loading'
            container
            direction='row'
            justifyContent='center'
            align='center'
            classes={{ root: classes.loadingAnimation }}
          >
            <CircularProgress size={60} color='primary' />
          </Grid>
        )}
        {!isLoading && skillBiteList.map((skillBite, key) => (
          <SkillBiteItem
            key={key}
            index={key}
            skillBite={skillBite}
          />
        ))}
        {(!isLoading && skillBiteList.lenght === 0) && (
          <Grid
            id='skill-bites-list-not-found'
            container
            direction='row'
            justifyContent='center'
            align='center'
            classes={{ root: classes.loadingAnimation }}
          >
            <Typography variant='h6'>No Skill Bite found!</Typography>
          </Grid>
        )}
        {(addSkillBiteList.isWorking) && (
          <Grid
            id='skill-bites-list-loading'
            container
            direction='row'
            justifyContent='center'
            align='center'
            classes={{ root: classes.loadingAnimation }}
          >
            <CircularProgress size={60} color='primary' />
          </Grid>
        )}
      </Paper>
      {!isLoading && !addSkillBiteList.isWorking && hasMoreSkillBites && (
        <Grid
          id='skill-bite-list-load-more'
          container
          direction='row'
          justifyContent='center'
          align='center'
          classes={{ root: classes.loadMoreBtn }}
        >
          <Grid item xs={12}>
            <Button variant='contained' ref={loadMoreRef} color='primary' name='' onClick={onFetchMoreSkillBites}>
              Load More
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  )
}

export default SkillBiteList
